import { forwardRef, useCallback, useEffect, useRef } from 'react';

import { FormHelperText, InputLabel, styled } from '@mui/material';
import {
  LocalizationProvider,
  TimePicker as MuiTimePicker,
  DesktopTimePickerProps,
} from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment, { Moment } from 'moment';

import { BaseFieldProps } from '../types';

const TimePickerRoot = styled('div')(() => ({
  flex: 1,
  minWidth: '100%',
  maxWidth: '100%',
  marginBottom: 8,
  '& .MuiFormControl-root': {
    width: '100%',
  },
}));

export interface BaseTimePickerProps
  extends BaseFieldProps,
    Omit<DesktopTimePickerProps<Moment>, 'onChange' | 'value'> {
  onChange?: (value: Moment | null) => void;
  value: Moment | null;
  error?: string;
}

export const TimePicker = forwardRef<HTMLInputElement, BaseTimePickerProps>(
  (props, ref) => {
    const { label, error, onChange, ...rest } = props;
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = useCallback(
      (element: HTMLInputElement | null) => {
        inputRef.current = element;

        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const currentTime = moment();
          if (onChange) {
            onChange(currentTime);
          }
        }
      };

      const inputElement = inputRef.current;
      if (inputElement) {
        inputElement.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        if (inputElement) {
          inputElement.removeEventListener('keydown', handleKeyDown);
        }
      };
    }, [onChange]);

    return (
      <TimePickerRoot>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          {label && <InputLabel>{label}</InputLabel>}
          <MuiTimePicker
            ampm={false}
            {...rest}
            inputRef={setRefs}
            sx={{ mt: 0 }}
            onChange={onChange}
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </LocalizationProvider>
      </TimePickerRoot>
    );
  },
);

TimePicker.displayName = 'Custom Time Picker';
